import React from 'react';
import { Box } from '@mui/material';
import { formatMoney } from '../../utils/localeFormat';
import { indianAmountInWords } from '../../utils/indianAmountInWords';

export interface PlatformTransactionRecord {
  _id?: string;
  tenantId?: string;
  tenant?: string;
  companyName?: string;
  plan?: string;
  planName?: string;
  amount: number;
  currency?: string;
  status?: string;
  reference?: string;
  date?: string | Date;
  paidAt?: string | Date;
  createdAt?: string | Date;
  tenantEmail?: string;
  tenantMobile?: string;
  tenantGst?: string;
  tenantAddress?: string;
  tenantCity?: string;
  tenantState?: string;
  tenantPincode?: string;
  tenantCountry?: string;
}

interface PlatformTransactionReceiptPrintProps {
  transaction: PlatformTransactionRecord;
  platform?: Record<string, any>;
}

const styles = {
  root: {
    backgroundColor: '#fff',
    color: '#111',
    width: '100%',
    maxWidth: 794,
    margin: '0 auto',
    padding: 24,
    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
    fontSize: 12,
    lineHeight: 1.45,
  } as React.CSSProperties,
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
  },
  cell: {
    border: '1px solid #333',
    padding: '8px 10px',
    fontSize: 12,
    verticalAlign: 'top' as const,
  },
  label: { fontWeight: 700, width: '38%' },
  title: { fontSize: 22, fontWeight: 700, margin: 0 },
  company: { fontSize: 18, fontWeight: 700, margin: '0 0 4px' },
};

function splitAddressLines(address?: string): string[] {
  if (!address) return [];
  return address.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
}

function formatTxnDate(value?: string | Date): string {
  if (!value) return '—';
  const d = typeof value === 'string' ? new Date(value) : value;
  return new Intl.DateTimeFormat('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(d);
}

function statusLabel(status?: string): string {
  if (!status) return '—';
  return status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export const PlatformTransactionReceiptPrint: React.FC<PlatformTransactionReceiptPrintProps> = ({
  transaction,
  platform,
}) => {
  const currency = transaction.currency || 'INR';
  const language = 'en';
  const formatAmount = (amount: number) =>
    formatMoney(amount, currency, language, { maximumFractionDigits: 2 });

  const platformName = platform?.companyName || 'Tripiigo';
  const platformAddressLines = splitAddressLines(platform?.address);
  const clientName = transaction.companyName || transaction.tenant || 'Client';
  const planName = transaction.planName || transaction.plan || '—';
  const paidOn = transaction.paidAt || transaction.date || transaction.createdAt;
  const receiptNo = transaction.reference || `RCP-${String(transaction._id || '').slice(-8).toUpperCase()}`;
  const amountInWords = indianAmountInWords(transaction.amount);
  const clientLocation = [
    transaction.tenantAddress,
    [transaction.tenantCity, transaction.tenantPincode].filter(Boolean).join(' '),
    transaction.tenantState,
    transaction.tenantCountry,
  ].filter(Boolean);

  return (
    <Box id="platform-transaction-receipt-print">
      <div style={styles.root}>
        <table style={{ ...styles.table, marginBottom: 16 }}>
          <tbody>
            <tr>
              <td style={{ border: 'none', padding: 0, verticalAlign: 'top', width: '55%' }}>
                {platform?.logo && (
                  <img
                    src={platform.logo}
                    alt={platformName}
                    style={{ maxHeight: 48, maxWidth: 160, marginBottom: 8, objectFit: 'contain' }}
                  />
                )}
                <p style={styles.company}>{platformName}</p>
                {platformAddressLines.map((line) => (
                  <div key={line}>{line}</div>
                ))}
                {platform?.gstNumber && <div style={{ marginTop: 4 }}>GSTIN : {platform.gstNumber}</div>}
                {platform?.email && <div>{platform.email}</div>}
              </td>
              <td style={{ border: 'none', padding: 0, verticalAlign: 'top', textAlign: 'right' }}>
                <p style={styles.title}>PAYMENT RECEIPT</p>
                <div style={{ marginTop: 8 }}>Receipt# {receiptNo}</div>
                <div style={{ marginTop: 4 }}>Date : {formatTxnDate(paidOn)}</div>
              </td>
            </tr>
          </tbody>
        </table>

        <div style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>Received From</div>
          <div style={{ fontWeight: 700, fontSize: 14 }}>{clientName}</div>
          {transaction.tenantEmail && <div>{transaction.tenantEmail}</div>}
          {transaction.tenantMobile && <div>{transaction.tenantMobile}</div>}
          {clientLocation.map((line) => (
            <div key={line}>{line}</div>
          ))}
          {transaction.tenantGst && <div style={{ marginTop: 4 }}>GSTIN {transaction.tenantGst}</div>}
          {transaction.tenantId && <div>Tenant ID : {transaction.tenantId}</div>}
        </div>

        <table style={styles.table}>
          <tbody>
            <tr>
              <td style={{ ...styles.cell, ...styles.label }}>Subscription Plan</td>
              <td style={styles.cell}>{planName}</td>
            </tr>
            <tr>
              <td style={{ ...styles.cell, ...styles.label }}>Payment Status</td>
              <td style={styles.cell}>{statusLabel(transaction.status)}</td>
            </tr>
            <tr>
              <td style={{ ...styles.cell, ...styles.label }}>Amount Received</td>
              <td style={{ ...styles.cell, fontWeight: 700, fontSize: 16 }}>{formatAmount(transaction.amount)}</td>
            </tr>
            <tr>
              <td style={{ ...styles.cell, ...styles.label }}>Currency</td>
              <td style={styles.cell}>{currency}</td>
            </tr>
            <tr>
              <td style={{ ...styles.cell, ...styles.label }}>Payment Date</td>
              <td style={styles.cell}>{formatTxnDate(paidOn)}</td>
            </tr>
            {transaction.createdAt && (
              <tr>
                <td style={{ ...styles.cell, ...styles.label }}>Recorded On</td>
                <td style={styles.cell}>{formatTxnDate(transaction.createdAt)}</td>
              </tr>
            )}
            {transaction._id && (
              <tr>
                <td style={{ ...styles.cell, ...styles.label }}>Transaction ID</td>
                <td style={styles.cell}>{String(transaction._id)}</td>
              </tr>
            )}
          </tbody>
        </table>

        <div style={{ marginTop: 16, marginBottom: 16 }}>
          <strong>Amount In Words:</strong>
          <br />
          {amountInWords}
        </div>

        <div style={{ marginTop: 8, fontSize: 12, color: '#444' }}>
          {platform?.invoiceFooter || 'Thank you for your payment. This receipt confirms subscription billing received by the platform.'}
        </div>

        <div style={{ marginTop: 32, textAlign: 'right' }}>
          {platform?.signature && (
            <img
              src={platform.signature}
              alt="Authorized signature"
              style={{ maxHeight: 56, maxWidth: 140, objectFit: 'contain', marginBottom: 4 }}
            />
          )}
          <div style={{ borderTop: '1px solid #333', display: 'inline-block', paddingTop: 4, fontWeight: 600 }}>
            Authorized Signature
          </div>
          <div style={{ fontSize: 11, marginTop: 4 }}>{platform?.authorizedSignatory || platformName}</div>
        </div>
      </div>
    </Box>
  );
};

export const printPlatformTransactionReceipt = () => {
  const content = document.getElementById('platform-transaction-receipt-print');
  if (!content) return;

  const printStyles = `
    @page { size: A4; margin: 12mm; }
    body { margin: 0; font-family: "Helvetica Neue", Helvetica, Arial, sans-serif; font-size: 12px; color: #111; }
    table { width: 100%; border-collapse: collapse; }
    th, td { border: 1px solid #333; padding: 8px 10px; font-size: 12px; }
    img { max-height: 56px; object-fit: contain; }
  `;

  const printWindow = window.open('', '_blank', 'width=900,height=700');
  if (!printWindow) return;
  printWindow.document.write(`
    <html><head><title>Payment Receipt</title><style>${printStyles}</style></head><body>${content.innerHTML}</body></html>
  `);
  printWindow.document.close();
  printWindow.focus();
  setTimeout(() => {
    printWindow.print();
    printWindow.close();
  }, 400);
};

export const downloadPlatformTransactionReceiptPdf = async (receiptNo: string) => {
  const { downloadElementAsPdf } = await import('../../utils/downloadPdf');
  const safeName = receiptNo.replace(/[^\w-]+/g, '_');
  await downloadElementAsPdf('platform-transaction-receipt-print', safeName);
};
